import { type FunctionComponent, useContext } from 'react';
import { Box } from '@atlaskit/primitives';
import Footer from '../Footer/Footer';
import MainContent from '../MainContent/MainContent';
import McpConsentLayout from '../MainContent/MCP/McpConsentLayout';
import TwgConsentLayout from '../MainContent/TWG/TwgConsentLayout';
import RovoConsentLayout from '../MainContent/Rovo/RovoConsentLayout';
import useRovoVisualRefresh from '../MainContent/Rovo/useRovoVisualRefresh';
import ConsentForm from './ConsentForm';
import {
  ReactConsentContext,
  narrowConsentContentStyle,
  useToken,
} from './ConsentContextProvider';

// Re-exported for back-compat with existing importers (e.g. VR fixtures).
export { narrowConsentContentStyle };

/**
 * Renders consent chrome: `<main>` is always the direct child of page `Content`
 * (same as pre-MCP-redesign), then `<form>`, so non-MCP layouts are unchanged.
 *
 * MCP owns its own layout via MainContent → McpConsentLayout, including its
 * own form, footer, and context providers.
 * This is needed as the style differs greatly from other consent flows
 */
const ConsentLayout: FunctionComponent = () => {
  const { serviceType } = useToken();
  const consentContext = useContext(ReactConsentContext);
  const rovoVisualRefresh = useRovoVisualRefresh();

  if (serviceType === 'MCP') {
    return <McpConsentLayout />;
  }

  if (serviceType === 'TWG') {
    return <TwgConsentLayout />;
  }

  // Refreshed Rovo flow uses its own layout (sticky action footer). The legacy
  // layout (below) is kept as the fallback when the gate is off.
  if (serviceType === 'Rovo' && rovoVisualRefresh) {
    return <RovoConsentLayout />;
  }

  return (
    <Box as="main" xcss={narrowConsentContentStyle}>
      <ConsentForm action={serviceType === 'DAG' ? '/oauth/activate' : undefined}>
        <MainContent />
        <Footer acceptDisabled={consentContext?.acceptDisabled} />
      </ConsentForm>
    </Box>
  );
};

export default ConsentLayout;
